import { ReactNode } from 'react'
import { Avatar, Badge, Chip, Grid, Stack, Typography, useTheme } from '@mui/material'
import { Exchange, exchangeToJSON } from '../../proto/generated/common'
import { LOGOS_BASE_URL } from '../../assets/constants'
import { getColorForSymbol } from '../../util/general'
import { searchRowInstrumentLabel, InstrumentLabels } from '../../util/cashInstrument'
import TextHighlighter from '../common/TextHighlighter'
import ExchangeLogos from './ExchangeLogos'
import TickIcon from '../../assets/icons/TickIcon'

const safeString = (v: unknown): string => (typeof v === 'string' ? v : '')

export interface SecurityRowCellsProps {
    symbol: string
    series?: string
    displayName: string
    exchange?: Exchange
    /** Exchanges shown as stacked logos (defaults to the row's own exchange). */
    exchanges?: Exchange[]
    instrument?: string
    instrumentLabels?: InstrumentLabels
    /** Rolling near-month future (X_M1): shown TradingView-style as "SYMBOL1!" with a ROLLING tag. */
    rolling?: boolean
    logo?: ReactNode
    logoUrl?: string
    colorSymbol?: string
    searchTerm: string
    /** Related (child) row: indented, no logo. */
    indent?: boolean
    /** Already picked (e.g. in the watchlist): green tick badge on the logo instead of a row highlight. */
    selected?: boolean
    /** Rendered in the trailing cell before the type label (e.g. compare-picker actions). */
    trailingStart?: ReactNode
    /** Rendered at the very end of the row (chevron, add / remove). */
    trailingEnd?: ReactNode
}

/**
 * The three cells every symbol-search row shares - the plain search row, related rows and the
 * compare picker all render through here so they look the same.
 */
const SecurityRowCells = ({
    symbol,
    series,
    displayName,
    exchange,
    exchanges,
    instrument,
    instrumentLabels,
    rolling = false,
    logo,
    logoUrl,
    colorSymbol,
    searchTerm,
    indent = false,
    selected = false,
    trailingStart,
    trailingEnd
}: SecurityRowCellsProps) => {
    const theme = useTheme()
    const avatar = indent ? null : logo ? (
        <Avatar alt={safeString(displayName)} sx={{ width: 24, height: 24, background: (t) => t.palette.background.default }}>
            {logo}
        </Avatar>
    ) : logoUrl ? (
        <Avatar alt={safeString(displayName)} src={`${LOGOS_BASE_URL}${logoUrl}.svg`} sx={{ width: 24, height: 24 }} />
    ) : (
        <Avatar
            alt={safeString(displayName)}
            sx={{ width: 24, height: 24, fontSize: 12, color: 'white', bgcolor: getColorForSymbol(colorSymbol ?? symbol, theme.palette.background.default) }}
        >
            {(symbol || '')
                .split(' ')
                .map((word) => word[0]?.toUpperCase())
                .slice(0, 2)
                .join('')}
        </Avatar>
    )
    return (
        <Grid container justifyContent={'center'} spacing={1}>
            <Grid item xs={4}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: indent ? 7 : 3, minWidth: 0 }}>
                    {avatar &&
                        (selected ? (
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                sx={{ '& .MuiBadge-badge': { p: 0, minWidth: 0, height: 'auto', top: 1, right: 1 } }}
                                badgeContent={
                                    <Stack
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: '50%',
                                            bgcolor: 'success.main',
                                            color: 'success.contrastText',
                                            border: (t) => `1px solid ${t.palette.background.paper}`,
                                            '& svg': { width: 11, height: 11 }
                                        }}
                                    >
                                        <TickIcon />
                                    </Stack>
                                }
                            >
                                {avatar}
                            </Badge>
                        ) : (
                            avatar
                        ))}
                    <Typography noWrap variant={'body1'} sx={{ lineHeight: '24px' }}>
                        <TextHighlighter textToHighlight={rolling ? safeString(symbol).replace(/\s+M1$/, '1!') : safeString(symbol)} searchTerm={searchTerm} />
                        {series && series !== 'EQ' && series !== 'XX' ? '-' + series : ''}
                    </Typography>
                    {rolling && (
                        <Chip
                            label="ROLLING"
                            size="small"
                            variant="outlined"
                            sx={{ height: 16, fontSize: 9, letterSpacing: 0.5, color: 'text.tertiary', borderColor: 'divider', '& .MuiChip-label': { px: 0.75 } }}
                        />
                    )}
                </Stack>
            </Grid>
            <Grid item xs={5} sx={{ minWidth: 0 }}>
                <Typography sx={{ lineHeight: '24px' }} variant={'body2'} noWrap>
                    <TextHighlighter textToHighlight={safeString(displayName)} searchTerm={searchTerm} />
                </Typography>
            </Grid>
            <Grid item xs={3}>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ pr: 2, lineHeight: '24px', minWidth: 0 }}>
                    {trailingStart}
                    <Typography component="span" noWrap variant={'body2'} sx={{ fontSize: 12, lineHeight: '24px', flexShrink: 1, minWidth: 0, color: 'text.tertiary' }}>
                        {searchRowInstrumentLabel(instrument, instrumentLabels)}
                    </Typography>
                    <Typography component="span" noWrap variant={'body2'} sx={{ lineHeight: '24px', flexShrink: 0 }}>
                        {exchangeToJSON(exchange ?? Exchange.NSE)}
                    </Typography>
                    <ExchangeLogos exchanges={exchanges ?? [exchange ?? Exchange.NSE]} />
                    {trailingEnd}
                </Stack>
            </Grid>
        </Grid>
    )
}

export default SecurityRowCells
